import { useCallback, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useUpdateServiceAccountListMutation } from '../data-layer/serviceIdentityApi';
import { type UpdateServiceAccountListProps } from '../data-layer/serviceIdentityType';

/**
 * Saves the service account list for a given service ID.
 *
 * @param serviceId - The ID of the service identity to save accounts for.
 * @returns An object containing `handleUpdateServiceAccountList`, `isSASaving`, and `isSASaveError`.
 */
const useUpdateServiceAccountList = (serviceId: number | undefined) => {
  const [isSASaving, setIsSASaving] = useState(false);

  // API hooks
  const [updateServiceAccountList, { isError: isSASaveError }] =
    useUpdateServiceAccountListMutation();

  const handleUpdateServiceAccountList = useCallback(
    async ({
      addAccounts,
      removeAccounts,
    }: {
      addAccounts?: { accountKey: number }[];
      removeAccounts?: { accountKey: number }[];
    }) => {
      setIsSASaving(true);

      try {
        if (!serviceId) {
          throw new Error('No service ID provided');
        }

        const payload: UpdateServiceAccountListProps = { serviceId };

        if (addAccounts) {
          payload.addAccounts = addAccounts;
        }

        if (removeAccounts) {
          payload.removeAccounts = removeAccounts;
        }

        const controller = updateServiceAccountList(payload);

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        Logger.log('Failed to save service account list', error);

        return 'Failed to save service account list';
      } finally {
        setIsSASaving(false);
      }
    },
    [updateServiceAccountList, serviceId]
  );

  return {
    handleUpdateServiceAccountList,
    isSASaveError,
    isSASaving,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useUpdateServiceAccountList };
